/**
 * The one Convex client for this app.
 *
 * Module scope, constructed once, exported as a value — not built inside a
 * component and not memoised. `ConvexProviderWithAuth` re-runs its auth
 * effects whenever the `client` prop's identity changes, so a client rebuilt
 * on render is a client that re-authenticates on render, and this app mounts
 * exactly that provider (`src/App.tsx`, with `useConvexLightAuth`).
 *
 * The URL comes through `@/env` rather than a raw `import.meta.env` read,
 * which is where this diverges from `infra/templates/convex/app/`. That
 * template throws here because it is the only reader of the variable; this
 * app already has one validated env surface (`src/env.schema.ts`, registered
 * in `infra/scripts/env-surfaces.ts`), and adding a second reader would mean
 * `bun run env:example` describing a variable whose real check lives
 * somewhere else. The throw still happens, one module earlier and with zod's
 * message: `VITE_CONVEX_URL` is a required `z.url()`, so an unset or
 * malformed value fails `env.ts`'s parse at module-eval time.
 *
 * Do NOT give it a fallback URL. A fallback turns "this is not configured"
 * into "this is silently talking to the wrong deployment", which is the one
 * failure mode that survives review — docs/convex.md makes the same argument
 * at more length.
 *
 * The socket is lazy. `ConvexReactClient`'s constructor validates the address
 * eagerly but opens nothing, so importing this module costs a URL parse.
 */
import { ConvexReactClient } from "convex/react";
import { env } from "@/env";

export const convex = new ConvexReactClient(env.VITE_CONVEX_URL);
